import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Plus, Search, FileText, RefreshCw, Loader2, Trash2, Edit2, Printer, Mail, MessageSquare
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import Pagination from '../../components/ui/pagination';
import SendEmailModal from '../../components/SendEmailModal';
import SendWhatsAppModal from '../../components/SendWhatsAppModal';
import EstimatePrintDocument, { formatEstimateWhatsAppText, formatEstimateEmailHtml } from './EstimatePrintDocument';
import { generateDocPdf } from '../../utils/generateDocPdf';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`
});

const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
  SENT: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
  ACCEPTED: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
  REJECTED: 'bg-red-100 text-red-700 hover:bg-red-200',
};

const formatCurrency = (v, cur = 'INR') => {
  if (!v && v !== 0) return '-';
  return `${cur === 'INR' ? '₹' : cur} ${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

export default function EstimateList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const offscreenPrintRef = useRef(null);

  const [estimates, setEstimates] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Status updating state per row
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  // Email Modal states
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailEstimate, setEmailEstimate] = useState(null);
  const [emailCompany, setEmailCompany] = useState(null);
  const [loadingEmailDoc, setLoadingEmailDoc] = useState(false);

  // WhatsApp Modal states
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [whatsappEstimate, setWhatsappEstimate] = useState(null);
  const [whatsappCompany, setWhatsappCompany] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.role === 'super_admin') {
        setIsSuperAdmin(true);
        fetch(`${API}/api/companies`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : [])
          .then(setCompanies);
      }
    } catch { /* token parse error */ }
  }, []);

  const fetchEstimates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (companyFilter !== 'all') params.append('company_id', companyFilter);
      params.append('skip', String((page - 1) * pageSize));
      params.append('limit', String(pageSize));
      const res = await fetch(`${API}/api/estimates?${params}`, { headers: headers() });
      if (res.ok) {
        const d = await res.json();
        setEstimates(d.items || []);
        setTotal(d.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [search, companyFilter, page, pageSize]);

  useEffect(() => { fetchEstimates(); }, [fetchEstimates]);
  useEffect(() => { setPage(1); }, [companyFilter]);
  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); fetchEstimates(); }, 300);
    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this estimate?')) return;
    await fetch(`${API}/api/estimates/${id}`, { method: 'DELETE', headers: headers() });
    fetchEstimates();
  };

  const handleStatusChange = async (estimateId, newStatus) => {
    setUpdatingStatusId(estimateId);
    try {
      const res = await fetch(`${API}/api/estimates/${estimateId}/status`, {
        method: 'PATCH',
        headers: headers(),
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update status');
      }
      const updated = await res.json();
      setEstimates(prev => prev.map(e => (e.id === estimateId ? { ...e, status: updated.status } : e)));
      toast({
        title: 'Estimate Status Updated',
        description: `Status changed to ${newStatus}${newStatus === 'ACCEPTED' ? ' (Customer Master synchronized)' : ''}`,
      });
    } catch (err) {
      toast({ title: 'Status Update Failed', description: err.message, variant: 'destructive' });
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleOpenEmail = async (estimateSummary) => {
    setLoadingEmailDoc(true);
    try {
      const res = await fetch(`${API}/api/estimates/${estimateSummary.id}`, { headers: headers() });
      if (!res.ok) throw new Error('Could not fetch full estimate details');
      const fullEst = await res.json();
      setEmailEstimate(fullEst);

      let cmp = fullEst.company || null;
      if (!cmp && fullEst.company_id) {
        cmp = companies.find(c => c.id === fullEst.company_id) || null;
      }
      if (!cmp) {
        try {
          const cmpRes = await fetch(`${API}/api/companies/${fullEst.company_id}`, { headers: headers() });
          if (cmpRes.ok) cmp = await cmpRes.json();
        } catch {}
      }
      setEmailCompany(cmp);
      setEmailModalOpen(true);
    } catch (err) {
      toast({ title: 'Email preparation error', description: err.message, variant: 'destructive' });
    } finally {
      setLoadingEmailDoc(false);
    }
  };

  const handleOpenWhatsApp = async (estimateSummary) => {
    try {
      const res = await fetch(`${API}/api/estimates/${estimateSummary.id}`, { headers: headers() });
      const fullEst = res.ok ? await res.json() : estimateSummary;
      setWhatsappEstimate(fullEst);
      const cmp = fullEst.company || companies.find(c => c.id === fullEst.company_id) || null;
      setWhatsappCompany(cmp);
      setWhatsappModalOpen(true);
    } catch {
      setWhatsappEstimate(estimateSummary);
      setWhatsappModalOpen(true);
    }
  };

  const pdfFetcher = async () => {
    if (!offscreenPrintRef.current) return null;
    return await generateDocPdf(offscreenPrintRef.current);
  };

  const companyName = (cid) => {
    const c = companies.find(x => x.id === cid);
    return c ? c.name : null;
  };

  return (
    <div className="space-y-6" data-testid="estimate-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-dark font-manrope">Estimates</h1>
          <p className="text-text-secondary font-ibm mt-1">Customer quotations and logistics estimates</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchEstimates} data-testid="refresh-estimates-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => navigate('/estimates/add')} className="bg-accent-blue hover:bg-accent-blue/90 font-ibm" data-testid="new-estimate-btn">
            <Plus className="w-4 h-4 mr-2" /> New Estimate
          </Button>
        </div>
      </div>

      {/* Filters Bar */}
      <Card className="p-4 bg-white border border-gray-100 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-sm flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <Input
              placeholder="Search by party name or estimate no..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 font-ibm"
              data-testid="estimate-search"
            />
          </div>

          {isSuperAdmin && companies.length > 0 && (
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger data-testid="estimate-company-filter" className="w-60 font-ibm">
                <SelectValue placeholder="All Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="bg-white border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
          </div>
        ) : estimates.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-text-dark font-semibold font-ibm">No estimates yet</p>
            <p className="text-text-secondary text-sm font-ibm mb-4">Create your first quotation</p>
            <Button onClick={() => navigate('/estimates/add')} className="bg-accent-blue font-ibm">
              <Plus className="w-4 h-4 mr-2" /> New Estimate
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="estimates-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Estimate No', 'Date', 'Party Name', ...(isSuperAdmin ? ['Company'] : []), 'Scope', 'Mode', 'Direction', 'Service', 'Cargo Type', 'Total', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {estimates.map(e => (
                  <tr key={e.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-semibold text-accent-blue font-ibm cursor-pointer" onClick={() => navigate(`/estimates/edit/${e.id}`)} data-testid={`estimate-no-${e.id}`}>
                      {e.estimate_no || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-dark font-ibm">
                      {e.estimate_date ? String(e.estimate_date).split('T')[0] : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-dark font-ibm max-w-[150px] truncate" title={e.party_name || ''}>
                      {e.party_name || '-'}
                    </td>
                    {isSuperAdmin && (
                      <td className="px-4 py-3 text-xs text-text-secondary font-ibm max-w-[160px] truncate" title={companyName(e.company_id) || ''}>
                        {companyName(e.company_id) || '—'}
                      </td>
                    )}
                    <td className="px-4 py-3 text-xs font-ibm">
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-semibold">{e.shipment_scope}</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-text-dark font-ibm">{e.mode_of_transport}</td>
                    <td className="px-4 py-3 text-xs font-ibm">
                      <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded font-semibold">{e.movement_direction}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm">{e.service || '-'}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm">{e.cargo_type || '-'}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-text-dark font-ibm">{formatCurrency(e.total_amount, e.currency)}</td>

                    {/* Interactive Status Dropdown */}
                    <td className="px-4 py-3">
                      <div className="relative inline-block">
                        <Select
                          value={e.status || 'DRAFT'}
                          onValueChange={(val) => handleStatusChange(e.id, val)}
                          disabled={updatingStatusId === e.id}
                        >
                          <SelectTrigger
                            className={`h-7 px-2.5 py-0.5 text-xs font-semibold rounded-full border-0 font-ibm shadow-none transition-all flex items-center gap-1.5 cursor-pointer ${
                              STATUS_COLORS[e.status] || STATUS_COLORS.DRAFT
                            }`}
                            data-testid={`estimate-status-trigger-${e.id}`}
                          >
                            {updatingStatusId === e.id ? (
                              <span className="flex items-center gap-1 text-[11px]">
                                <Loader2 className="w-3 h-3 animate-spin" /> Saving...
                              </span>
                            ) : (
                              <SelectValue />
                            )}
                          </SelectTrigger>
                          <SelectContent align="start">
                            <SelectItem value="DRAFT" className="text-xs font-ibm cursor-pointer">Draft</SelectItem>
                            <SelectItem value="SENT" className="text-xs font-ibm text-blue-700 font-semibold cursor-pointer">Sent</SelectItem>
                            <SelectItem value="ACCEPTED" className="text-xs font-ibm text-emerald-700 font-semibold cursor-pointer">Accepted</SelectItem>
                            <SelectItem value="REJECTED" className="text-xs font-ibm text-red-700 font-semibold cursor-pointer">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </td>

                    {/* Actions column */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/estimates/edit/${e.id}`)}
                          data-testid={`edit-estimate-${e.id}`}
                          title="Edit Estimate"
                          className="h-8 w-8 p-0"
                        >
                          <Edit2 className="w-4 h-4 text-accent-blue" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/estimates/print/${e.id}`, '_blank')}
                          data-testid={`print-estimate-${e.id}`}
                          title="Print / View Quotation"
                          className="h-8 w-8 p-0"
                        >
                          <Printer className="w-4 h-4 text-text-secondary" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEmail(e)}
                          disabled={loadingEmailDoc}
                          data-testid={`email-estimate-${e.id}`}
                          title="Send Quotation via Email (with PDF)"
                          className="h-8 w-8 p-0 text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenWhatsApp(e)}
                          data-testid={`whatsapp-estimate-${e.id}`}
                          title="Send Quotation via WhatsApp"
                          className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(e.id)}
                          data-testid={`delete-estimate-${e.id}`}
                          title="Delete Estimate"
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4 text-accent-red" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && estimates.length > 0 && (
          <Pagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} />
        )}
      </Card>

      {/* Hidden offscreen container for PDF generation */}
      {emailEstimate && (
        <div
          ref={offscreenPrintRef}
          style={{
            position: 'fixed',
            left: '-9999px',
            top: 0,
            width: '794px',
            background: '#fff',
            zIndex: -999,
          }}
        >
          <EstimatePrintDocument estimate={emailEstimate} company={emailCompany} />
        </div>
      )}

      {/* Email Modal */}
      {emailEstimate && (
        <SendEmailModal
          isOpen={emailModalOpen}
          onClose={() => {
            setEmailModalOpen(false);
            setEmailEstimate(null);
          }}
          templateKey="estimate_quotation"
          variables={{
            party_name: emailEstimate.party_name || 'Customer',
            estimate_no: emailEstimate.estimate_no || '',
            estimate_date: emailEstimate.estimate_date ? String(emailEstimate.estimate_date).split('T')[0] : '',
            shipment_scope: emailEstimate.shipment_scope || '',
            mode_of_transport: emailEstimate.mode_of_transport || '',
            movement_direction: emailEstimate.movement_direction || '',
            origin: emailEstimate.aol || emailEstimate.road_from || emailEstimate.pickup_address || '',
            destination: emailEstimate.aod || emailEstimate.road_to || emailEstimate.delivery_at || emailEstimate.road_location || '',
            total_amount: `${emailEstimate.currency === 'INR' ? '₹' : (emailEstimate.currency || 'INR')} ${Number(emailEstimate.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            company_name: emailCompany?.name || 'STAVAN INTERNATIONAL LLP',
          }}
          defaultToEmail={emailEstimate.party_email || ''}
          defaultSubject={`Quotation ${emailEstimate.estimate_no || ''} — ${emailEstimate.party_name || 'Customer'}`}
          defaultBody={formatEstimateEmailHtml(emailEstimate, emailCompany)}
          pdfFetcher={pdfFetcher}
          pdfFilename={`${emailEstimate.estimate_no || 'estimate'}.pdf`}
          documentTitle={`Quotation — ${emailEstimate.estimate_no || ''}`}
        />
      )}

      {/* WhatsApp Modal */}
      {whatsappEstimate && (
        <SendWhatsAppModal
          isOpen={whatsappModalOpen}
          onClose={() => {
            setWhatsappModalOpen(false);
            setWhatsappEstimate(null);
          }}
          title="Send Quotation via WhatsApp"
          documentNumber={whatsappEstimate.estimate_no || ''}
          partyName={whatsappEstimate.party_name || ''}
          defaultMobile={whatsappEstimate.party_phone || ''}
          defaultMessage={formatEstimateWhatsAppText(whatsappEstimate, whatsappCompany)}
          infoBadges={[
            { label: 'Amount', value: `${whatsappEstimate.currency === 'INR' ? '₹' : (whatsappEstimate.currency || 'INR')} ${Number(whatsappEstimate.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
            { label: 'Mode', value: `${whatsappEstimate.mode_of_transport || 'AIR'}` },
          ]}
        />
      )}
    </div>
  );
}
